import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import apiClient from '../services/apiClient';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  updateTheme: (newTheme: Theme) => Promise<void>;
  isDark: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();

  // Initialize theme from user profile, localStorage, or default to light
  const [theme, setTheme] = useState<Theme>(() => {
    // If user is authenticated and has a theme preference, use it
    if (isAuthenticated && user?.theme) {
      return user.theme;
    }

    // Otherwise, try localStorage
    try {
      const savedTheme = localStorage.getItem('theme') as Theme;
      return savedTheme || 'light';
    } catch (error) {
      // Fallback to light theme if localStorage fails
      console.warn('Failed to read theme from localStorage:', error);
      return 'light';
    }
  });

  // Sync theme with user profile when user changes
  useEffect(() => {
    if (isAuthenticated && user?.theme && user.theme !== theme) {
      setTheme(user.theme);
    }
  }, [isAuthenticated, user?.theme, theme]);

  // Apply theme to document root and save to localStorage
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', theme);

    // Always save to localStorage as backup
    try {
      localStorage.setItem('theme', theme);
    } catch (error) {
      console.warn('Failed to save theme to localStorage:', error);
    }
  }, [theme]);

  // Update theme and save to user profile if authenticated
  const updateTheme = async (newTheme: Theme) => {
    setTheme(newTheme);

    // If user is authenticated, save to profile
    if (isAuthenticated) {
      try {
        await apiClient.put('/api/users/profile', { theme: newTheme });
      } catch (error) {
        console.error('Failed to save theme to user profile:', error);
        // Theme is still updated locally and in localStorage
      }
    }
  };

  const toggleTheme = async () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    await updateTheme(newTheme);
  };

  const isDark = theme === 'dark';

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, updateTheme, isDark }}>
      {children}
    </ThemeContext.Provider>
  );
};
